import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { palette, transition, admin, shadow } from '../../styles/tokens';
import { BOOKING_STATUSES } from './config';
import { canonicalMoney, sumMoney } from '../../lib/money';
import type { BookingRow, BookingSummary } from './hooks';

type FilterKey = 'all' | 'upcoming' | 'completed' | 'no_show' | 'cancelled';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'no_show', label: 'No-Show' },
  { key: 'cancelled', label: 'Cancelled' },
];

interface Props {
  bookings: BookingRow[];
  summary: BookingSummary;
  onSelect: (booking: BookingRow) => void;
}

function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function fmtDate(d: string): string {
  const date = new Date(d + 'T12:00:00');
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays === -1) return 'Tomorrow';
  return date.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });
}

export function BookingHistoryList({ bookings, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');

  const filtered = useMemo(() => {
    let list = bookings;
    const today = new Date().toISOString().split('T')[0];

    if (filter === 'upcoming') list = list.filter(b => b.date >= today && b.status !== 'cancelled' && b.status !== 'no_show');
    else if (filter === 'completed') list = list.filter(b => b.status === 'completed');
    else if (filter === 'no_show') list = list.filter(b => b.status === 'no_show');
    else if (filter === 'cancelled') list = list.filter(b => b.status === 'cancelled');

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        (b.client_name ?? '').toLowerCase().includes(q) ||
        (b.service_name ?? '').toLowerCase().includes(q) ||
        (b.assigned_name ?? '').toLowerCase().includes(q),
      );
    }

    return list.sort((a, b) => b.date.localeCompare(a.date) || b.start_time.localeCompare(a.start_time));
  }, [bookings, filter, search]);

  // Group by date
  const grouped = useMemo(() => {
    const map = new Map<string, BookingRow[]>();
    for (const b of filtered) {
      const arr = map.get(b.date) ?? [];
      arr.push(b);
      map.set(b.date, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const uniqueClients = new Set(filtered.map(b => b.client_name)).size;
  const totalRevenue = Number(sumMoney(filtered.map(b => canonicalMoney(b.payment_amount ?? 0))));
  const noShows = filtered.filter(b => b.status === 'no_show').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Booking History</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Search and filter past and upcoming bookings</div>
        </div>
      </div>

      {/* Answer bar */}
      <div style={{ display: 'flex', gap: 24, padding: '12px 20px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, marginBottom: 16, fontSize: 13 }}>
        <span><strong style={{ color: palette.g800 }}>{filtered.length}</strong> <span style={{ color: palette.g500 }}>bookings</span></span>
        <span><strong style={{ color: palette.g800 }}>${totalRevenue.toLocaleString()}</strong> <span style={{ color: palette.g500 }}>revenue</span></span>
        <span><strong style={{ color: palette.g800 }}>{uniqueClients}</strong> <span style={{ color: palette.g500 }}>clients</span></span>
        {noShows > 0 && <span><strong style={{ color: palette.red }}>{noShows}</strong> <span style={{ color: palette.g500 }}>no-shows</span></span>}
      </div>

      {/* Search + filter pills */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
          <Search size={14} color={palette.g400} style={{ position: 'absolute', left: 10, top: 10 }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client, service, operator..."
            style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', color: palette.g800, boxSizing: 'border-box' as const }} />
        </div>
        <div style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2 }}>
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '5px 14px', borderRadius: 5, border: 'none', fontSize: 12, fontWeight: 500,
              fontFamily: 'inherit', cursor: 'pointer',
              background: filter === f.key ? palette.g800 : 'transparent',
              color: filter === f.key ? palette.white : palette.g500,
              transition: `all ${transition.fast}`,
            }}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Grouped list */}
      <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, boxShadow: shadow.card, overflow: 'hidden' }}>
        {grouped.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: palette.g400, fontSize: 13 }}>
            {search ? 'No bookings match your search' : 'No bookings found'}
          </div>
        ) : grouped.map(([date, rows]) => (
          <div key={date}>
            <div style={{ padding: '10px 16px', background: palette.g50, fontSize: 12, fontWeight: 600, color: palette.g500, borderBottom: `1px solid ${palette.g100}` }}>
              {fmtDate(date)}
            </div>
            {rows.map(b => {
              const st = BOOKING_STATUSES[b.status] ?? BOOKING_STATUSES.pending;
              return (
                <div key={b.id} onClick={() => onSelect(b)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                  borderBottom: `1px solid ${palette.g100}`, cursor: 'pointer',
                  transition: `background ${transition.fast}`,
                }} onMouseEnter={e => (e.currentTarget.style.background = palette.g50)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <span style={{ fontSize: 12, color: palette.g500, width: 70, flexShrink: 0 }}>{fmtTime(b.start_time)}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: palette.g800, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.client_name ?? 'Walk-in'} — {b.service_name ?? 'Service'}
                  </span>
                  <span style={{ fontSize: 12, color: palette.g500, flexShrink: 0 }}>{b.assigned_name ?? ''}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg, flexShrink: 0 }}>{st.label}</span>
                  {b.payment_amount != null && <span style={{ fontSize: 12, fontWeight: 500, color: palette.g600, flexShrink: 0, width: 50, textAlign: 'right' }}>${b.payment_amount}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
